/**
 * Site-mode helper.
 *
 * NaturePulse and FarmersPulse share the exact same codebase and backend.
 * The active "face" is determined in this order:
 *   1. Build-time env `REACT_APP_SITE` (used by local dev + private previews).
 *      Set it to `farmerspulse` to force the fork:
 *
 *        REACT_APP_SITE=farmerspulse
 *
 *   2. Runtime hostname (used by the SAME deployment serving both domains):
 *      any host containing `farmerspulse` → FarmersPulse; everything else
 *      falls back to NaturePulse.
 *
 * This module is the single source of truth for site-aware behaviour —
 * routes, nav, home page and marketing copy read from it.
 */

function _detectSite() {
  const envSite = (process.env.REACT_APP_SITE || "").toLowerCase();
  if (envSite) return envSite;
  if (typeof window !== "undefined") {
    const host = (window.location.hostname || "").toLowerCase();
    if (host.includes("farmerspulse")) return "farmerspulse";
  }
  return "naturepulse";
}

const SITE = _detectSite();

export const IS_FARMERSPULSE = SITE === "farmerspulse";
export const IS_NATUREPULSE = !IS_FARMERSPULSE;

/**
 * Site-aware client-side path helper.
 *
 * Rewrites the given sub-path (e.g. "/farms/new", "/today", "/ask") so that
 * Link / navigate() calls resolve on both the standalone FarmersPulse fork
 * (root URLs) and the embedded NaturePulse routes ("/farmers/..."). API
 * paths (http.get/http.post) MUST NOT be routed through this — they always
 * use "/farmers/..." because the backend is single-tenant.
 *
 * Usage:
 *   import { fpPath } from "@/lib/site";
 *   <Link to={fpPath("/farms/new")}>…</Link>
 *   navigate(fpPath(`/farms/${id}`));
 *   fpPath("/") → home for the current site
 */
export function fpPath(sub) {
  if (!sub || sub === "/") return IS_FARMERSPULSE ? "/" : "/farmers";
  const s = sub.startsWith("/") ? sub : `/${sub}`;
  return IS_FARMERSPULSE ? s : `/farmers${s}`;
}

export const NATUREPULSE_URL = process.env.REACT_APP_NATUREPULSE_URL || "https://naturepulse.net";
export const FARMERSPULSE_URL = process.env.REACT_APP_FARMERSPULSE_URL || "https://farmerspulse.com";

export const SITE_META = IS_FARMERSPULSE
  ? {
      site: "farmerspulse",
      brand: "FarmersPulse",
      brandShort: "Farmers Pulse",
      tagline: "The daily desk for growers.",
      description:
        "Crop insights, market signals, weather alerts and community observations — a working desk for farmers, from the SocialPulse network.",
      homePath: "/",
      externalHome: FARMERSPULSE_URL,
      emails: {
        support: "support@farmerspulse.com",
        contact: "contact@farmerspulse.com",
        info:    "info@farmerspulse.com",
        legal:   "legal@farmerspulse.com",
        admin:   "admin@farmerspulse.com",
        noreply: "noreply@farmerspulse.com",
      },
      counterpart: {
        label: "NaturePulse",
        url: NATUREPULSE_URL,
        prompt: "Explore the living planet",
      },
    }
  : {
      site: "naturepulse",
      brand: "NaturePulse",
      brandShort: "Nature Pulse",
      tagline: "Today's weather. Tomorrow's history.",
      description:
        "A global archive of weather, wildlife and everyday nature observations. Track. Share. Preserve.",
      homePath: "/",
      externalHome: NATUREPULSE_URL,
      emails: {
        support: "contact@naturepulse.net",
        contact: "contact@naturepulse.net",
        info:    "hello@naturepulse.net",
        legal:   "privacy@naturepulse.net",
        admin:   "contact@naturepulse.net",
        noreply: "noreply@naturepulse.net",
      },
      counterpart: {
        label: "FarmersPulse",
        url: FARMERSPULSE_URL,
        prompt: "The daily desk for growers",
      },
    };
